import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MOODS } from "../../lib/constants";
import { useLanguage } from "../../context/LanguageContext";

export default function EmotionRing({ currentMood, onChange, isReadOnly = false, size = "md" }) {
    const { t } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);

    const activeMood = MOODS.find(m => m.label === currentMood || m.id === currentMood) || MOODS.find(m => m.id === 'neutral');
    const sizeClasses = size === "lg" ? "w-16 h-16" : size === "sm" ? "w-8 h-8" : "w-10 h-10";
    const ringWidth = size === "lg" ? "border-4" : "border-2";

    if (isReadOnly) {
        return (
            <div className={`relative ${sizeClasses} rounded-full flex items-center justify-center border-2 border-transparent`}>
                <div className={`absolute inset-0 rounded-full ${activeMood.bg} opacity-20 animate-pulse`} />
                <div className={`absolute inset-0 rounded-full border-2 ${activeMood.border}`} />
                <activeMood.icon className={`w-1/2 h-1/2 ${activeMood.color}`} />
            </div>
        );
    }

    return (
        <div className="relative z-50">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`relative ${sizeClasses} rounded-full flex items-center justify-center transition-all hover:scale-105`}
            >
                <div className={`absolute inset-0 rounded-full ${activeMood.bg} opacity-10`} />
                <div className={`absolute inset-0 rounded-full ${ringWidth} ${activeMood.border}`} />
                <activeMood.icon className={`w-1/2 h-1/2 ${activeMood.color}`} />

                {/* Pulse Effect for Visibility */}
                <div className={`absolute -inset-1 rounded-full ${activeMood.bg} opacity-20 animate-ping`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

                        {/* Menu - Aligned to Right to prevent overflow */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: 10, x: 0 }}
                            animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                            exit={{ opacity: 0, scale: 0.8, y: 10, x: 0 }}
                            className="absolute top-full mt-2 right-0 bg-white dark:bg-gray-800 p-2 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 flex flex-col gap-1 min-w-[140px] z-50 origin-top-right"
                        >
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider text-center py-1">{t('dash_mood_current_state') || 'Current State'}</p>
                            {MOODS.map((m) => (
                                <button
                                    key={m.id}
                                    onClick={() => {
                                        onChange(m.label);
                                        setIsOpen(false);
                                    }}
                                    className={`flex items-center gap-3 p-2 rounded-xl text-sm transition-colors ${(currentMood === m.id || currentMood === m.label)
                                        ? "bg-gray-100 dark:bg-gray-700 font-medium"
                                        : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                        }`}
                                >
                                    <m.icon className={`w-4 h-4 ${m.color}`} />
                                    <span className="text-gray-700 dark:text-gray-200">{t(m.titleKey || 'mood_' + m.id)}</span>
                                </button>
                            ))}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
